import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash, PencilSimple } from "@phosphor-icons/react";

const inputCls = "bg-zinc-950 border-zinc-800 rounded-sm text-zinc-100 focus:border-emerald-600 focus-visible:ring-emerald-600";
const TABS = [
  { key: "departments", label: "Departments" },
  { key: "designations", label: "Designations" },
  { key: "categories", label: "Task Categories" },
  { key: "priorities", label: "Priorities" },
  { key: "vendors", label: "Vendors" },
  { key: "document_types", label: "Document Types" },
  { key: "po_template", label: "PO Template" },
  { key: "roles", label: "Team Roles" },
  { key: "projects", label: "Projects" },
  { key: "holidays", label: "Holiday Calendar" },
  { key: "field_labels", label: "Detail Field Labels" },
  { key: "form_labels", label: "Form Labels" },
  { key: "custom_fields", label: "Custom Task Fields" },
  { key: "missing_fields", label: "Missing Fields" },
  { key: "escalation", label: "Escalation Default" },
];

function DocumentTypesEditor() {
  const [items, setItems] = useState([]);
  const [name, setName] = useState("");
  const [q, setQ] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");
  const load = () => api.get("/document_types").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const shown = items.filter((i) => !q || i.name.toLowerCase().includes(q.toLowerCase()));
  const add = async () => { if (!name.trim()) return; await api.post("/document_types", { name: name.trim() }); setName(""); toast.success("Added"); load(); };
  const toggleReq = async (i) => { await api.put(`/document_types/${i.id}`, { name: i.name, required: !i.required }); toast.success(i.required ? "Marked optional" : "Marked required"); load(); };
  const del = async (id) => { await api.delete(`/document_types/${id}`); toast.success("Removed"); load(); };
  const startEdit = (i) => { setEditingId(i.id); setEditName(i.name); };
  const saveEdit = async (i) => {
    if (!editName.trim()) return toast.error("Name cannot be empty");
    try {
      await api.put(`/document_types/${i.id}/rename`, { name: editName.trim() });
      toast.success("Renamed — every existing file under the old name has moved to the new name automatically");
      setEditingId(null); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Rename failed"); }
  };
  return (
    <div className="max-w-2xl">
      <p className="text-sm text-zinc-400 mb-4">Document tabs available when uploading order documents (PO Copy, Tender Copy, etc.). Toggle <span className="text-zinc-200">Required</span> to block an order from advancing until that document is uploaded.</p>
      <div className="inline-flex items-center gap-1.5 text-[11px] text-emerald-400 bg-emerald-500/10 border border-emerald-500/30 rounded-full px-2.5 py-1 mb-4" data-testid="auto-saved-indicator-document_types">
        <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" /> Every add / edit / delete here saves instantly — nothing to lose on refresh or restart.
      </div>
      <div className="flex gap-2 mb-4 flex-wrap items-center">
        <div className="flex gap-2 max-w-md flex-1 min-w-[240px]">
          <Input value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} placeholder="New document type…" className={inputCls} data-testid="document_types-input" />
          <Button onClick={add} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm gap-1" data-testid="document_types-add"><Plus weight="bold" size={16} /> Add</Button>
        </div>
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" className={`${inputCls} w-52 ml-auto`} data-testid="document_types-search" />
      </div>
      <div className="space-y-2">
        {shown.map((i) => (
          <div key={i.id} className="flex items-center justify-between border border-zinc-800 bg-zinc-900/30 px-3 py-2.5" data-testid={`doctype-${i.id}`}>
            {editingId === i.id ? (
              <div className="flex items-center gap-2 flex-1 mr-4">
                <Input value={editName} onChange={(e) => setEditName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && saveEdit(i)} autoFocus className={`${inputCls} h-8 text-sm`} data-testid={`doctype-edit-input-${i.id}`} />
                <Button size="sm" onClick={() => saveEdit(i)} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm h-8" data-testid={`doctype-save-${i.id}`}>Save</Button>
                <Button size="sm" variant="outline" onClick={() => setEditingId(null)} className="border-zinc-800 rounded-sm h-8" data-testid={`doctype-cancel-${i.id}`}>Cancel</Button>
              </div>
            ) : (
              <span className="text-sm text-zinc-200 flex items-center gap-2">
                {i.name}
                <button onClick={() => startEdit(i)} className="text-zinc-600 hover:text-emerald-400 transition-colors" title="Edit name" data-testid={`doctype-edit-${i.id}`}><PencilSimple size={13} /></button>
              </span>
            )}
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 text-xs text-zinc-400 cursor-pointer">
                <Switch checked={!!i.required} onCheckedChange={() => toggleReq(i)} className="data-[state=checked]:bg-emerald-600" data-testid={`doctype-required-${i.id}`} />
                {i.required ? <span className="text-emerald-400 font-semibold">Required</span> : "Optional"}
              </label>
              <button onClick={() => del(i.id)} className="text-zinc-600 hover:text-red-400 transition-colors" data-testid={`del-${i.id}`}><Trash size={15} /></button>
            </div>
          </div>
        ))}
        {shown.length === 0 && <div className="text-sm text-zinc-500 py-4">{items.length === 0 ? "No document types yet." : "No matches for your search."}</div>}
      </div>
    </div>
  );
}

function POTemplateEditor() {
  const [meta, setMeta] = useState(null);
  const load = () => api.get("/settings/po-template").then((r) => setMeta(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const upload = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    if (!f.name.toLowerCase().endsWith(".xlsx")) return toast.error("Upload a .xlsx Excel file");
    if (f.size > 4 * 1024 * 1024) return toast.error("Max 4MB");
    const r = new FileReader();
    r.onload = async () => {
      try { await api.put("/settings/po-template", { name: f.name, data: r.result }); toast.success("PO template saved — generated POs will now use it"); load(); }
      catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
    };
    r.readAsDataURL(f);
    e.target.value = "";
  };
  const remove = async () => {
    if (!window.confirm("Remove the PO template? Generated POs will fall back to the default layout.")) return;
    await api.delete("/settings/po-template"); toast.success("Template removed"); load();
  };
  const PLACEHOLDERS = [
    ["{{ORDER_NO}}", "Order number"], ["{{ORDER_NAME}}", "Order name"], ["{{CUSTOMER}}", "Customer name"],
    ["{{CUSTOMER_CODE}}", "Customer code"], ["{{CUSTOMER_PO}}", "Customer PO no"], ["{{VENDOR}}", "Selected vendor"],
    ["{{DATE}}", "Generation date"], ["{{GENERATED_BY}}", "Generated by"], ["{{TOTAL}}", "Grand total"],
    ["{{ITEMS}}", "Line-items table is inserted at this cell (header + rows + total)"],
  ];
  return (
    <div className="max-w-2xl">
      <p className="text-sm text-zinc-400 mb-4">Upload your company's Excel stationery (header, logo cells, footer, terms). Generated POs will use this file, fill in the placeholders below and insert the line-items table where <span className="font-mono text-emerald-400">{"{{ITEMS}}"}</span> is placed.</p>
      <div className="inline-flex items-center gap-1.5 text-[11px] text-emerald-400 bg-emerald-500/10 border border-emerald-500/30 rounded-full px-2.5 py-1 mb-4" data-testid="auto-saved-indicator-po_template">
        <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" /> Uploads / removals here save instantly.
      </div>
      {meta?.exists ? (
        <div className="flex items-center justify-between border border-zinc-800 bg-zinc-900/30 px-4 py-3 mb-4 rounded-sm" data-testid="po-template-meta">
          <div>
            <div className="text-sm text-zinc-100 font-semibold">{meta.name}</div>
            <div className="text-xs text-zinc-500">Uploaded by {meta.uploaded_by} · {meta.uploaded_at?.slice(0, 10)}</div>
          </div>
          <button onClick={remove} className="text-zinc-600 hover:text-red-400 transition-colors" data-testid="po-template-remove"><Trash size={16} /></button>
        </div>
      ) : (
        <div className="text-xs text-zinc-500 mb-4" data-testid="po-template-empty">No template uploaded — POs use the built-in default layout.</div>
      )}
      <label className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm px-4 py-2 text-sm font-semibold cursor-pointer" data-testid="po-template-upload">
        <Plus weight="bold" size={15} /> {meta?.exists ? "Replace Template (.xlsx)" : "Upload Template (.xlsx)"}
        <input type="file" accept=".xlsx" className="hidden" onChange={upload} />
      </label>
      <div className="mt-6 border border-zinc-800 bg-zinc-900/30 rounded-sm p-4">
        <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold mb-2">Supported placeholders (put these in any cell)</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
          {PLACEHOLDERS.map(([k, d]) => (
            <div key={k} className="text-xs"><span className="font-mono text-emerald-400">{k}</span> <span className="text-zinc-400">— {d}</span></div>
          ))}
        </div>
      </div>
    </div>
  );
}

function EscalationEditor() {
  const [hours, setHours] = useState(2);
  useEffect(() => { api.get("/settings/escalation").then((r) => setHours(r.data.hours)); }, []);
  const save = async () => { await api.put("/settings/escalation", { hours: Number(hours) }); toast.success("Company default escalation updated"); };
  const runDigest = async () => {
    try { const { data } = await api.post("/admin/run-daily-digest"); toast.success(`Digest sent to ${data.digests_sent} member(s)`); }
    catch { toast.error("Digest failed"); }
  };
  return (
    <div className="max-w-md">
      <p className="text-sm text-zinc-400 mb-4">Company-wide default: escalate an overdue task to its manager after this many hours (used when a task has no per-task value). Level-2 escalation to admin fires at double this time.</p>
      <label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1 block">Escalate after (hours overdue)</label>
      <Input type="number" min="1" value={hours} onChange={(e) => setHours(e.target.value)} className={`${inputCls} max-w-[160px]`} data-testid="escalation-hours" />
      <div><Button onClick={save} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm mt-4" data-testid="save-escalation">Save Default</Button></div>
      <div className="mt-8 pt-6 border-t border-zinc-800">
        <div className="text-xs font-bold uppercase tracking-wider text-zinc-400 mb-2">Daily Stage-Owner Digest</div>
        <p className="text-sm text-zinc-400 mb-3">Every day at 09:30 IST each stage owner receives an email + in-app digest of their pending workflow stages (overdue ones highlighted). Unassigned active stages go to admins.</p>
        <Button onClick={runDigest} variant="outline" className="border-zinc-800 bg-zinc-950 rounded-sm" data-testid="run-digest">Send Digest Now</Button>
      </div>
    </div>
  );
}

function CustomFieldsEditor() {
  const [fields, setFields] = useState([]);
  useEffect(() => { api.get("/settings/custom-fields").then((r) => setFields(r.data.fields || [])); }, []);
  const add = () => setFields([...fields, { key: `cf_${Date.now()}`, label: "", type: "text" }]);
  const setF = (i, v) => setFields(fields.map((f, idx) => (idx === i ? { ...f, label: v } : f)));
  const del = (i) => setFields(fields.filter((_, idx) => idx !== i));
  const save = async () => { await api.put("/settings/custom-fields", { fields: fields.filter((f) => f.label.trim()) }); toast.success("Custom task fields saved"); };
  return (
    <div className="max-w-xl">
      <p className="text-sm text-zinc-400 mb-4">Add brand-new fields that appear on the Create Task form. Values are stored per task and shown on the task detail.</p>
      {fields.map((f, i) => (
        <div key={f.key} className="flex items-center gap-2 mb-2">
          <Input value={f.label} onChange={(e) => setF(i, e.target.value)} placeholder="Field label (e.g. Drawing No)" className={inputCls} data-testid={`cf-input-${i}`} />
          <button onClick={() => del(i)} className="text-zinc-600 hover:text-red-400"><Trash size={15} /></button>
        </div>
      ))}
      <div className="flex gap-2 mt-3">
        <Button onClick={add} variant="outline" className="border-zinc-800 bg-zinc-950 rounded-sm gap-1" data-testid="cf-add"><Plus size={14} /> Add Field</Button>
        <Button onClick={save} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm" data-testid="cf-save">Save Fields</Button>
      </div>
    </div>
  );
}

function FormLabelsEditor() {
  const [labels, setLabels] = useState(null);
  const [hidden, setHidden] = useState([]);
  const [order, setOrder] = useState([]);
  const [custom, setCustom] = useState([]);
  const [required, setRequired] = useState([]);
  const [requiredHints, setRequiredHints] = useState({});
  const [dragIdx, setDragIdx] = useState(null);
  const originalCustomLabels = useRef({});
  // Company Name feeds order.name and DVEPL Ref Code is the primary business identifier — both
  // are structurally load-bearing, so they can be renamed but never fully hidden from the form.
  const LOCKED_VISIBLE = ["customer", "customer_code"];
  const FIELDS = [
    { k: "department", d: "Department" }, { k: "category", d: "Category" },
    { k: "project", d: "Project / Customer" }, { k: "ref_code", d: "Ref Code" },
    { k: "customer_name", d: "Customer Name" }, { k: "priority", d: "Priority" },
  ];
  const ORDER_OPTIONAL = [
    { k: "customer", lk: "order_customer", d: "Customer Name" },
    { k: "customer_code", lk: "order_code", d: "Code" },
    { k: "contact_person", lk: "order_contact_person", d: "Contact Person" },
    { k: "customer_email", lk: "order_email", d: "Customer Email" },
    { k: "customer_whatsapp", lk: "order_whatsapp", d: "Customer WhatsApp" },
    { k: "customer_gst", lk: "order_customer_gst", d: "GST Number" },
    { k: "billing_address", lk: "order_billing_address", d: "Billing Address" },
    { k: "customer_po", lk: "order_customer_po", d: "Customer PO No" },
    { k: "order_date", lk: "order_order_date", d: "Date of Order" },
    { k: "target_dispatch", lk: "order_target_dispatch", d: "Date of Commitment" },
    { k: "taken_by", lk: "order_taken_by", d: "Order Taken By" },
    { k: "job_responsibility", lk: "order_job_responsibility", d: "Job Responsibility" },
  ];
  useEffect(() => {
    api.get("/settings/form-labels").then((r) => setLabels(r.data));
    api.get("/settings/order-form").then((r) => {
      setHidden(r.data.hidden || []); setOrder(r.data.order || []); setCustom(r.data.custom || []); setRequired(r.data.required || []);
      setRequiredHints(r.data.required_hints || {});
      originalCustomLabels.current = Object.fromEntries((r.data.custom || []).map((c) => [c.id, c.label]));
    }).catch(() => {});
  }, []);
  if (!labels) return <div className="text-sm text-zinc-500">Loading…</div>;
  const toggleHidden = (k) => setHidden((h) => {
    const next = h.includes(k) ? h.filter((x) => x !== k) : [...h, k];
    if (next.includes(k)) setRequired((r) => r.filter((x) => x !== k)); // can't require a hidden field
    return next;
  });
  const toggleRequired = (k) => setRequired((r) => {
    if (r.includes(k)) { setRequiredHints((h) => { const { [k]: _drop, ...rest } = h; return rest; }); return r.filter((x) => x !== k); }
    return [...r, k];
  });
  const seq = (order.length ? order : ORDER_OPTIONAL.map((f) => f.k))
    .map((k) => ORDER_OPTIONAL.find((f) => f.k === k)).filter(Boolean)
    .concat(ORDER_OPTIONAL.filter((f) => order.length && !order.includes(f.k)));
  const move = (idx, dir) => {
    const keys = seq.map((f) => f.k);
    const j = idx + dir;
    if (j < 0 || j >= keys.length) return;
    [keys[idx], keys[j]] = [keys[j], keys[idx]];
    setOrder(keys);
  };
  const save = async () => {
    await api.put("/settings/form-labels", { labels });
    const cleanedCustom = custom.filter((c) => c.label.trim());
    const renames = cleanedCustom
      .map((c) => ({ id: c.id, old: originalCustomLabels.current[c.id], new: c.label.trim() }))
      .filter((r) => r.old && r.old !== r.new)
      .map(({ old, new: newLabel }) => ({ old, new: newLabel }));
    const { data } = await api.put("/settings/order-form", { hidden, order: seq.map((f) => f.k), custom: cleanedCustom, required, required_hints: requiredHints, renames });
    originalCustomLabels.current = Object.fromEntries(cleanedCustom.map((c) => [c.id, c.label.trim()]));
    const notes = [];
    if (data.orders_notified) notes.push(`notified the owner(s) of ${data.orders_notified} in-progress order(s) missing a newly-mandatory field`);
    if (data.orders_renamed) notes.push(`carried the renamed field's value forward on ${data.orders_renamed} existing order(s)`);
    toast.success(notes.length ? `Order form saved — ${notes.join(" and ")}` : "Order form updated — labels, order, visibility, mandatory fields & custom fields saved");
  };
  return (
    <div className="max-w-2xl">
      <div className="border border-emerald-600/40 bg-emerald-600/5 rounded-sm p-4">
        <div className="text-xs font-bold uppercase tracking-wider text-emerald-400 mb-1">Start New Order form — rename / drag to arrange / show / hide</div>
        <p className="text-xs text-zinc-500 mb-3">This controls the order form. Type in a row to rename the field, drag ⠿ (or ▲▼) to re-arrange, switch off "Visible" to remove it from the form, switch on "Mandatory" to block Save & Proceed until that field is filled in.</p>
        <div className="grid grid-cols-1 gap-2">
          <div className="flex items-center gap-3 border border-zinc-800 bg-zinc-950/60 px-3 py-2 rounded-sm" data-testid="field-vis-order_name">
            <span className="text-zinc-700 select-none text-sm tracking-tighter">⠿</span>
            <span className="w-6" />
            <Input value={labels.order_name ?? ""} placeholder="Order Name" onChange={(e) => setLabels({ ...labels, order_name: e.target.value })} className={`${inputCls} h-8 flex-1`} data-testid="label-order_name" />
            <span className="text-xs text-zinc-500 shrink-0">Always shown</span>
          </div>
          {seq.map((f, idx) => (
            <div key={f.k} draggable
              onDragStart={() => setDragIdx(idx)}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => {
                if (dragIdx === null || dragIdx === idx) return;
                const keys = seq.map((x) => x.k);
                const [mv] = keys.splice(dragIdx, 1);
                keys.splice(idx, 0, mv);
                setOrder(keys); setDragIdx(null);
              }}
              className={`flex items-center gap-3 border px-3 py-2 rounded-sm cursor-grab active:cursor-grabbing transition-colors ${dragIdx === idx ? "border-emerald-600 bg-emerald-600/10 opacity-70" : "border-zinc-800 bg-zinc-950/60"}`}
              data-testid={`field-vis-${f.k}`}>
              <span className="text-zinc-600 select-none text-sm tracking-tighter" title="Drag to re-arrange">⠿</span>
              <span className="flex flex-col -my-1">
                <button onClick={() => move(idx, -1)} disabled={idx === 0} data-testid={`field-up-${f.k}`} className="text-zinc-600 hover:text-emerald-400 disabled:opacity-20 text-[10px] leading-tight">▲</button>
                <button onClick={() => move(idx, 1)} disabled={idx === seq.length - 1} data-testid={`field-down-${f.k}`} className="text-zinc-600 hover:text-emerald-400 disabled:opacity-20 text-[10px] leading-tight">▼</button>
              </span>
              <Input value={labels[f.lk] ?? ""} placeholder={f.d}
                onChange={(e) => setLabels({ ...labels, [f.lk]: e.target.value })}
                className={`${inputCls} h-8 flex-1`} data-testid={`label-${f.lk}`} />
              <span className="flex items-center gap-2 text-xs text-zinc-400 shrink-0">
                <Switch checked={!hidden.includes(f.k)} disabled={LOCKED_VISIBLE.includes(f.k)} onCheckedChange={() => toggleHidden(f.k)} className="data-[state=checked]:bg-emerald-600" data-testid={`field-vis-switch-${f.k}`} />
                {LOCKED_VISIBLE.includes(f.k) ? <span className="text-zinc-500" title="Required by the app — Order Name is built from this field, it can't be hidden">Always Visible</span>
                  : hidden.includes(f.k) ? <span className="text-zinc-500">Hidden</span> : <span className="text-emerald-400 font-semibold">Visible</span>}
              </span>
              <span className="flex items-center gap-2 text-xs text-zinc-400 shrink-0 border-l border-zinc-800 pl-3">
                <Switch checked={required.includes(f.k)} disabled={hidden.includes(f.k)} onCheckedChange={() => toggleRequired(f.k)} className="data-[state=checked]:bg-red-500" data-testid={`field-req-switch-${f.k}`} />
                {required.includes(f.k) ? <span className="text-red-400 font-semibold">Mandatory</span> : <span className="text-zinc-500">Optional</span>}
              </span>
              {required.includes(f.k) && (
                <Input value={requiredHints[f.k] || ""} onChange={(e) => setRequiredHints({ ...requiredHints, [f.k]: e.target.value })}
                  placeholder="Why is this required? (optional hint shown on hover)" className={`${inputCls} h-8 w-56 shrink-0 text-xs`} data-testid={`field-req-hint-${f.k}`} />
              )}
            </div>
          ))}
        </div>
      </div>
      <div className="mt-4 border border-zinc-800 bg-zinc-900/30 rounded-sm p-4">
        <div className="text-xs font-bold uppercase tracking-wider text-zinc-400 mb-1">Your custom order fields</div>
        <p className="text-xs text-zinc-500 mb-3">Add extra inputs to the Start New Order form — choose Text, Number, Date or Dropdown (comma-separated options). Filled values are saved into the order's detail tabs. Toggle Mandatory to require it too, with an optional hover hint.</p>
        {custom.map((c, i) => (
          <div key={c.id} className="mb-2.5 border border-zinc-800/60 rounded-sm p-2" data-testid={`custom-field-${c.id}`}>
            <div className="flex items-center gap-2 flex-wrap">
              <Input value={c.label} onChange={(e) => setCustom(custom.map((x, xi) => (xi === i ? { ...x, label: e.target.value } : x)))} placeholder="Field name, e.g. Contact Person" className={`${inputCls} h-8 flex-1 min-w-[160px]`} data-testid={`custom-label-${c.id}`} />
              <Select value={c.type || "text"} onValueChange={(v) => setCustom(custom.map((x, xi) => (xi === i ? { ...x, type: v } : x)))}>
                <SelectTrigger className={`${inputCls} h-8 w-32 text-xs`} data-testid={`custom-type-${c.id}`}><SelectValue /></SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-50">
                  <SelectItem value="text">Text</SelectItem>
                  <SelectItem value="number">Number</SelectItem>
                  <SelectItem value="date">Date</SelectItem>
                  <SelectItem value="dropdown">Dropdown</SelectItem>
                </SelectContent>
              </Select>
              {(c.type || "text") === "dropdown" && (
                <Input value={c.options || ""} onChange={(e) => setCustom(custom.map((x, xi) => (xi === i ? { ...x, options: e.target.value } : x)))} placeholder="Options: New, Existing" className={`${inputCls} h-8 flex-1 min-w-[160px]`} data-testid={`custom-options-${c.id}`} />
              )}
              <label className="flex items-center gap-1.5 text-xs text-zinc-400 cursor-pointer shrink-0">
                <Switch checked={!!c.required} onCheckedChange={(v) => setCustom(custom.map((x, xi) => (xi === i ? { ...x, required: v, hint: v ? x.hint : "" } : x)))} className="data-[state=checked]:bg-red-500" data-testid={`custom-required-${c.id}`} />
                {c.required ? <span className="text-red-400 font-semibold">Mandatory</span> : "Optional"}
              </label>
              <button onClick={() => setCustom(custom.filter((_, xi) => xi !== i))} data-testid={`custom-del-${c.id}`} className="text-zinc-600 hover:text-red-400"><Trash size={14} /></button>
            </div>
            {c.required && (
              <Input value={c.hint || ""} onChange={(e) => setCustom(custom.map((x, xi) => (xi === i ? { ...x, hint: e.target.value } : x)))}
                placeholder="Why is this required? (optional hint shown on hover)" className={`${inputCls} h-8 mt-1.5 text-xs`} data-testid={`custom-hint-${c.id}`} />
            )}
          </div>
        ))}
        <button onClick={() => setCustom([...custom, { id: `cf_${Date.now()}`, label: "", type: "text", options: "" }])} data-testid="add-custom-field" className="text-xs text-emerald-400 hover:underline mt-1">+ Add custom field</button>
        <div className="mt-3">
          <Button onClick={save} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm" data-testid="save-custom-fields">Save Custom Fields</Button>
          <p className="text-[11px] text-zinc-500 mt-1.5">Renames, additions and deletions above only take effect once you click Save — including removing a field for good.</p>
        </div>
      </div>
      <div className="mt-4 border border-zinc-800 bg-zinc-900/30 rounded-sm p-4">
        <div className="text-xs font-bold uppercase tracking-wider text-zinc-400 mb-1">Create Task form labels — tasks only, NOT the order form</div>
        <p className="text-xs text-zinc-500 mb-3">These rename fields on the New Task form. They have no effect on Start New Order.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {FIELDS.map((f) => (
            <div key={f.k}>
              <label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1 block">{f.d}</label>
              <Input value={labels[f.k] ?? f.d} onChange={(e) => setLabels({ ...labels, [f.k]: e.target.value })} className={inputCls} data-testid={`label-${f.k}`} />
            </div>
          ))}
        </div>
      </div>
      <Button onClick={save} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm mt-4" data-testid="save-form-labels">Save Labels</Button>
    </div>
  );
}

function MasterList({ resource }) {
  const [items, setItems] = useState([]);
  const [name, setName] = useState("");
  const [q, setQ] = useState("");
  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState("");
  const load = () => api.get(`/${resource}`).then((r) => setItems(r.data));
  useEffect(() => { load(); }, [resource]);
  const shown = items.filter((i) => !q || i.name.toLowerCase().includes(q.toLowerCase()));

  const add = async () => {
    if (!name.trim()) return;
    await api.post(`/${resource}`, { name: name.trim() });
    setName(""); toast.success("Added"); load();
  };
  const del = async (id) => { await api.delete(`/${resource}/${id}`); toast.success("Removed"); load(); };
  const saveEdit = async (id) => {
    if (!editName.trim()) return;
    await api.put(`/${resource}/${id}`, { name: editName.trim() });
    setEditId(null); toast.success("Updated"); load();
  };

  return (
    <div>
      <div className="inline-flex items-center gap-1.5 text-[11px] text-emerald-400 bg-emerald-500/10 border border-emerald-500/30 rounded-full px-2.5 py-1 mb-3" data-testid={`auto-saved-indicator-${resource}`}>
        <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" /> Every add / edit / delete here saves instantly — nothing to lose on refresh or restart.
      </div>
      <div className="flex gap-2 mb-4 flex-wrap items-center">
        <div className="flex gap-2 max-w-md flex-1 min-w-[240px]">
          <Input value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} placeholder={`New ${resource.slice(0, -1)}…`} className={inputCls} data-testid={`${resource}-input`} />
          <Button onClick={add} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm gap-1" data-testid={`${resource}-add`}><Plus weight="bold" size={16} /> Add</Button>
        </div>
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" className={`${inputCls} w-52 ml-auto`} data-testid={`${resource}-search`} />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
        {shown.map((i) => (
          <div key={i.id} className="flex items-center justify-between border border-zinc-800 bg-zinc-900/30 px-3 py-2.5 group hover:border-zinc-700 transition-colors">
            {editId === i.id ? (
              <div className="flex items-center gap-1 w-full">
                <Input value={editName} onChange={(e) => setEditName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && saveEdit(i.id)} className={`${inputCls} h-7 text-sm`} autoFocus />
                <button onClick={() => saveEdit(i.id)} className="text-emerald-400 hover:text-emerald-300 text-xs px-1">Save</button>
              </div>
            ) : (
              <>
                <span className="text-sm text-zinc-200">{i.name}</span>
                <div className="flex items-center gap-2">
                  <button onClick={() => { setEditId(i.id); setEditName(i.name); }} className="text-zinc-600 hover:text-emerald-400 transition-colors" data-testid={`edit-${i.id}`}><PencilSimple size={14} /></button>
                  <button onClick={() => del(i.id)} className="text-zinc-600 hover:text-red-400 transition-colors" data-testid={`del-${i.id}`}><Trash size={15} /></button>
                </div>
              </>
            )}
          </div>
        ))}
        {shown.length === 0 && <div className="text-sm text-zinc-500 col-span-full py-4">{items.length === 0 ? "No entries yet." : "No matches for your search."}</div>}
      </div>
    </div>
  );
}

function MissingFieldsViewer() {
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);
  const load = () => api.get("/settings/order-form/missing").then((r) => setRows(r.data)).catch(() => setRows([]));
  useEffect(() => { load(); }, []);
  if (rows === null) return <div className="text-sm text-zinc-500">Loading…</div>;
  return (
    <div className="max-w-3xl">
      <p className="text-xs text-zinc-500 mb-3">Every in-progress order still missing one of today's mandatory fields (from the Form Labels tab above) — no need to wait for the one-time nudge notification, check back here anytime.</p>
      {rows.length === 0 ? (
        <div className="text-sm text-zinc-500 border border-zinc-800 bg-zinc-900/30 rounded-sm p-6 text-center" data-testid="missing-fields-empty">Nothing missing — every in-progress order has all its mandatory fields filled in.</div>
      ) : (
        <div className="border border-zinc-800 divide-y divide-zinc-800" data-testid="missing-fields-list">
          {rows.map((r) => (
            <div key={r.order_id} className="flex items-center justify-between gap-3 px-4 py-3 bg-zinc-900/30" data-testid={`missing-fields-row-${r.order_id}`}>
              <div className="min-w-0">
                <div className="text-sm text-zinc-100 font-medium truncate">{r.order_no} — {r.name}</div>
                <div className="text-xs text-red-400 mt-0.5">Missing: {r.missing.join(", ")}</div>
              </div>
              <Button size="sm" onClick={() => navigate(`/orders/${r.order_id}?edit=1&focus=${r.focus_field}`)}
                className="shrink-0 bg-emerald-700 hover:bg-emerald-800 text-white rounded-sm gap-1" data-testid={`missing-fields-fill-${r.order_id}`}>
                <PencilSimple size={14} /> Fill it in now
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Masters() {
  return (
    <div className="p-6 lg:p-8 max-w-[1400px] animate-fade-in">
      <div className="text-[10px] uppercase tracking-[0.2em] text-emerald-600 font-bold mb-1">Configuration</div>
      <h1 className="font-heading text-3xl font-black tracking-tight text-zinc-50 mb-6">Masters</h1>
      <Tabs defaultValue="departments">
        <TabsList className="bg-zinc-900 border border-zinc-800 rounded-sm flex-wrap h-auto">
          {TABS.map((t) => (
            <TabsTrigger key={t.key} value={t.key} className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white rounded-sm text-xs" data-testid={`mtab-${t.key}`}>{t.label}</TabsTrigger>
          ))}
        </TabsList>
        {TABS.map((t) => (
          <TabsContent key={t.key} value={t.key} className="mt-6">
            {t.key === "form_labels" ? <FormLabelsEditor />
              : t.key === "custom_fields" ? <CustomFieldsEditor />
              : t.key === "missing_fields" ? <MissingFieldsViewer />
              : t.key === "escalation" ? <EscalationEditor />
              : t.key === "document_types" ? <DocumentTypesEditor />
              : t.key === "po_template" ? <POTemplateEditor />
              : <MasterList resource={t.key} />}
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
